import React, { useState, useEffect, useCallback } from "react";
import { Grid, CardHeader, CardContent, Box, TextField, Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { LicenseServices } from "../../services/AllServices";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { IMAGE_BASE } from "../../utils/constants";
import * as Yup from "yup";


function EditLotteryLicence() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const fetchTaskData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await LicenseServices.getById();
            setData(response?.data?.data[0]);
        } catch (error) {
            errorHandler(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTaskData();
    }, [fetchTaskData]);


    const validationSchema = Yup.object().shape({
        licenseNumber: Yup.string()
        .matches(/^[a-zA-Z0-9]+$/, "Only letters and numbers are allowed, no spaces.")
        .required("Licence number is required"),
        licenseIssueAuthority: Yup.string().required("Issuing authority is required"),
        licenseExpiryDate: Yup.string().required("Expiry date is required"),
        file: Yup.mixed()
            .test("fileType", "Only PDF, JPG, and PNG files are allowed", (value) => {
                if (!value || typeof value === "string") return true; // allow already uploaded file
                return ["application/pdf", "image/jpeg", "image/png"].includes(value.type);
            })
    });
    

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            licenseNumber: data?.licenseNumber || "",
            licenseIssueAuthority: data?.licenseIssueAuthority || "",
            licenseExpiryDate: data?.licenseExpiryDate || "",
            file: data?.licenseDocument || "",
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const formData = new FormData();

                if (values.file && typeof values.file !== "string") {
                    formData.append("license", values.file);
                }

                formData.append("licenseNumber", values.licenseNumber);
                formData.append("licenseIssueAuthority", values.licenseIssueAuthority);
                formData.append("licenseExpiryDate", values.licenseExpiryDate);

                if (id) {
                    formData.append("ticketId", id);
                }

                const response = await LicenseServices.edit(formData);

                toast.success(response?.data?.message, { toastId: "success" });
                // navigate("/tickets-management");
            } catch (error) {
                errorHandler(error);
                setLoading(false);
            }
        }
    });

    const { values, touched, errors, handleChange, handleSubmit, setFieldValue } = formik;

    const handleFileChange = (event) => {
        const file = event.currentTarget.files[0];
        if (file) {
            const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
            if (allowedTypes.includes(file.type)) {
                setFieldValue("file", file);
            } else {
                toast.error("Only PDF, JPG, and PNG files are allowed.");
            }
        }
    };

    const getPreviewUrl = () => {
        if (values.file && typeof values.file !== "string") {
            return URL.createObjectURL(values.file);
        } else if (data?.licenseDocument) {
            return `${IMAGE_BASE}${data.licenseDocument}`;
        }
        return null;
    };

    return (
        <Box>
            <CardLayout>
                <CardHeader title={`Update Licence`} />
                <CardContent>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <FormikProvider value={formik}>
                            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Licence Number"
                                            name="licenseNumber"
                                            value={values.licenseNumber}
                                            onChange={handleChange}
                                            required
                                            error={Boolean(touched.licenseNumber && errors.licenseNumber)}
                                            helperText={touched.licenseNumber && errors.licenseNumber}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Licence Issue Authority"
                                            name="licenseIssueAuthority"
                                            value={values.licenseIssueAuthority}
                                            onChange={handleChange}
                                            required
                                            error={Boolean(touched.licenseIssueAuthority && errors.licenseIssueAuthority)}
                                            helperText={touched.licenseIssueAuthority && errors.licenseIssueAuthority}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <MobileDatePicker
                                            label="Licence Expiry Date"
                                            inputFormat="YYYY-MM-DD"
                                            value={values.licenseExpiryDate ? dayjs(values.licenseExpiryDate) : null}
                                            minDate={dayjs()}
                                            onChange={(val) =>
                                                formik.setFieldValue(
                                                    "licenseExpiryDate",
                                                    val ? val.format("YYYY-MM-DD") : ""
                                                )
                                            }
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: Boolean(touched.licenseExpiryDate && errors.licenseExpiryDate),
                                                    helperText: touched.licenseExpiryDate && errors.licenseExpiryDate,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Button variant="contained" component="label">
                                            Upload Licence File
                                            <input
                                                type="file"
                                                hidden
                                                accept=".pdf, image/*"
                                                onChange={handleFileChange}
                                            />
                                        </Button>
                                        {touched.file && errors.file && (
                                            <Box color="error.main" mt={1}>
                                                {errors.file}
                                            </Box>
                                        )}

                                        {/* Image/File Preview */}
                                        {getPreviewUrl() && (
                                            <Box mt={2}>
                                                {typeof values.file === "string" && values.file.endsWith(".pdf") ? (
                                                    <a
                                                        href={getPreviewUrl()}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        View Uploaded PDF
                                                    </a>
                                                ) : (
                                                    <img
                                                        src={getPreviewUrl()}
                                                        alt="Licence Preview"
                                                        style={{ maxWidth: "100%", maxHeight: 400 }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box mt={3}>
                                            <LoadingButton
                                                fullWidth
                                                size="large"
                                                type="submit"
                                                variant="contained"
                                                // loading={loading}
                                            >
                                                Update Licence
                                            </LoadingButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Form>
                        </FormikProvider>
                    </LocalizationProvider>
                </CardContent>
            </CardLayout>
        </Box>
    );
}

export default EditLotteryLicence;
